import React from 'react';
import { Link } from 'react-router-dom';
import OptimizedImage from '../components/OptimizedImage';
import SEOHead from '../components/SEOHead';
import { imageConfig, altTexts } from '../utils/imageConfig';

const AIJourney = () => {
  return (
    <div className="ai-journey">
      <SEOHead 
        title="AI for You - Saner Consulting | AI Implementation & Transformation"
        description="Discover how AI can transform your business with Saner Consulting's comprehensive AI implementation services. From strategy to deployment, we guide your AI transformation journey."
        keywords="AI implementation, artificial intelligence, AI transformation, machine learning, AI strategy, business AI, AI consulting, UK"
        canonical="https://sanerconsulting.co.uk/ai-journey"
      />
      
      {/* Hero Section */}
      <section className="hero section">
        <div className="container">
          <div className="grid grid-2" style={{ alignItems: 'center', gap: '3rem' }}>
            <div className="hero-content fade-in-up">
              <h1 className="hero-title">AI for You</h1>
              <p className="subtitle fade-in-up" style={{animationDelay: '0.2s'}}>
                Unlock the transformative power of AI for your business. From strategic planning to implementation, 
                we guide you through every step of your AI transformation journey.
              </p>
              <div className="hero-actions fade-in-up" style={{animationDelay: '0.4s'}}>
                <Link to="/contact#contact-form" className="btn btn-primary">
                  Start Your AI Transformation
                </Link>
                <Link to="/services" className="btn btn-secondary">
                  Explore Our Services
                </Link>
              </div>
            </div>
            <div className="hero-image fade-in-up" style={{animationDelay: '0.3s'}}>
              <OptimizedImage
                src={imageConfig.hero.aiRobot}
                alt="AI Robot - Artificial Intelligence Technology"
                width="100%"
                height="400px"
                className="hero-main-image"
                style={{ objectFit: 'cover', aspectRatio: '16/9' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* AI Solutions Overview */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Your AI Transformation Journey</h2>
          <div className="grid grid-3">
            <div className="card">
              <OptimizedImage
                src={imageConfig.technology.nlpProcessing}
                alt={altTexts.technology.nlpProcessing}
                width="100%"
                height="200px"
                className="card-image"
                style={{ objectFit: 'cover', aspectRatio: '16/9' }}
              />
              <h3 className="card-title">AI Strategy & Planning</h3>
              <p className="card-description">
                Develop a comprehensive AI strategy aligned with your business objectives and transformation goals.
              </p>
            </div>
            <div className="card">
              <OptimizedImage
                src={imageConfig.technology.aiImplementation}
                alt="AI implementation and deployment solutions"
                width="100%"
                height="200px"
                className="card-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">AI Implementation</h3>
              <p className="card-description">
                From proof of concept to full-scale deployment, we ensure successful AI implementation across your organization.
              </p>
            </div>
            <div className="card">
              <OptimizedImage
                src={imageConfig.technology.aiDrivenAutomation}
                alt="AI-driven automation and intelligent process optimization"
                width="100%"
                height="200px"
                className="card-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">AI-Driven Automation</h3>
              <p className="card-description">
                Transform your operations with intelligent automation that adapts and learns from your business processes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Benefits */}
      <section className="section section-dark">
        <div className="container">
          <h2 className="section-title">Why AI Matters for Your Business</h2>
          <div className="grid grid-2">
            <div className="card">
              <h3 className="card-title">Competitive Advantage</h3>
              <p className="card-description">
                Stay ahead of the competition by leveraging AI to optimize operations, 
                enhance customer experiences, and drive innovation.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Operational Efficiency</h3>
              <p className="card-description">
                Automate routine tasks, reduce errors, and free up your team to focus 
                on high-value activities that drive business growth.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Data-Driven Insights</h3>
              <p className="card-description">
                Transform raw data into actionable insights that inform strategic 
                decisions and reveal new business opportunities.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Scalable Solutions</h3>
              <p className="card-description">
                Implement AI solutions that grow with your business, adapting to 
                changing needs and expanding capabilities over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Coaching Approach */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Our AI Transformation Coaching</h2>
          <p className="section-description">
            We don't just implement AI - we coach your organization through the cultural and operational 
            changes needed to maximize AI's potential.
          </p>
          <div className="grid grid-3" style={{ marginTop: '2rem' }}>
            <div className="card">
              <h3 className="card-title">1. Assessment & Strategy</h3>
              <p className="card-description">
                Evaluate your current capabilities and define an AI roadmap 
                tailored to your business objectives.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">2. Team Development</h3>
              <p className="card-description">
                Build AI literacy across your organization and develop 
                the skills needed for successful adoption.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">3. Implementation & Support</h3>
              <p className="card-description">
                Guide the deployment of AI solutions with ongoing support 
                to ensure sustainable success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="section-title">Ready to Start Your AI Journey?</h2>
            <p className="subtitle">
              Connect with our AI transformation experts to explore how artificial intelligence 
              can drive your business forward.
            </p>
            <div className="cta-buttons">
              <Link to="/contact#contact-form" className="btn btn-primary">
                Start Your AI Transformation
              </Link>
              <Link to="/services" className="btn btn-secondary">
                Explore Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIJourney;